import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, RefreshCw, Sunrise, X } from 'lucide-react'
import { api, RecurrenceType, ReminderItem } from '../api/client'
import { EmptyState } from '../components/ui/Empty'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'

const WAKE_GROUP = 'Без даты'

function getGroup(r: ReminderItem): string {
  if (r.trigger_type === 'on_wake' || !r.remind_at) return WAKE_GROUP
  const d = new Date(r.remind_at)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const tomorrow = new Date(today.getTime() + 86400_000)
  const weekEnd = new Date(today.getTime() + 7 * 86400_000)
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  if (day.getTime() === today.getTime()) return 'Сегодня'
  if (day.getTime() === tomorrow.getTime()) return 'Завтра'
  if (day <= weekEnd) return 'На этой неделе'
  return 'Позже'
}

function isOverdue(r: ReminderItem): boolean {
  if (r.trigger_type === 'on_wake' || !r.remind_at) return false
  return new Date(r.remind_at) < new Date()
}

const GROUP_ORDER = ['Сегодня', 'Завтра', 'На этой неделе', 'Позже', WAKE_GROUP]

const RECURRENCE_LABEL: Record<RecurrenceType, string> = {
  daily:    'Ежедневно',
  weekly:   'Еженедельно',
  monthly:  'Ежемесячно',
  weekdays: 'По будням',
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

function formatDayNum(iso: string): string {
  return new Date(iso).getDate().toString()
}

function formatMonthShort(iso: string): string {
  return new Date(iso).toLocaleDateString('ru', { month: 'short' }).replace('.', '')
}

export function Reminders() {
  const qc = useQueryClient()
  const { haptic, confirm, close } = useTelegram()

  const { data, isLoading, error, refetch } = useQuery<ReminderItem[]>({
    queryKey: ['reminders'],
    queryFn: () => api.get('/api/reminders'),
    staleTime: 30_000,
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/reminders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  })

  const handleCancel = (r: ReminderItem) => {
    haptic.impactOccurred('light')
    confirm('Отменить напоминание?', ok => {
      if (ok) cancelMutation.mutate(r.id)
    })
  }

  const grouped = GROUP_ORDER.reduce<Record<string, ReminderItem[]>>((acc, g) => {
    acc[g] = (data ?? []).filter(r => getGroup(r) === g)
    return acc
  }, {} as Record<string, ReminderItem[]>)

  const isEmpty = !isLoading && !error && (data ?? []).length === 0

  // Find first upcoming reminder for accent highlight
  const firstUpcomingId = (data ?? []).find(r => r.trigger_type !== 'on_wake' && !isOverdue(r))?.id

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Напоминания</p>
        {data && data.length > 0 && (
          <p className="text-sm text-ink-2 mt-0.5">{data.length} активных</p>
        )}
      </div>

      <div className="flex-1 px-4 pb-6 space-y-1">
        {isLoading && Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)}

        {error && <ErrorCard onRetry={refetch} />}

        {isEmpty && (
          <EmptyState
            icon={<Bell size={40} />}
            title="Активных напоминаний нет"
            description={'Скажи Delo, когда о чём напомнить:\n\n«напомни через час позвонить маме»\n«напомни завтра в 9 отправить отчёт»'}
            cta="Открыть чат с Delo"
            onCta={close}
          />
        )}

        {GROUP_ORDER.map(group => {
          const items = grouped[group]
          if (!items || items.length === 0) return null
          return (
            <div key={group}>
              <p className="section-label pt-4">{group}</p>
              <div className="space-y-2">
                {items.map(r => {
                  const isWake = r.trigger_type === 'on_wake' || !r.remind_at
                  const overdue = isOverdue(r)
                  const isNext = r.id === firstUpcomingId
                  return (
                    <div
                      key={r.id}
                      className={`rounded-xl p-4 flex items-center gap-4 transition-all border border-line-1 ${
                        overdue ? 'opacity-50' : ''
                      }`}
                      style={
                        isNext
                          ? { borderLeft: '3px solid var(--color-accent)', background: 'var(--color-accent-dim-2)' }
                          : overdue
                          ? { borderLeft: '3px solid var(--color-danger)' }
                          : { background: 'var(--color-bg-1)' }
                      }
                    >
                      {/* Date block */}
                      <div className="shrink-0 text-center w-10">
                        {isWake ? (
                          <Sunrise size={22} className="mx-auto text-accent" />
                        ) : (
                          <>
                            <p className={`text-xl font-semibold leading-none tabular-nums ${
                              overdue ? 'text-danger' : isNext ? 'text-accent' : 'text-ink'
                            }`}>
                              {formatDayNum(r.remind_at!)}
                            </p>
                            <p className="text-xs text-ink-2 mt-0.5 capitalize">
                              {formatMonthShort(r.remind_at!)}
                            </p>
                          </>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-base leading-snug ${overdue ? 'text-danger' : 'text-ink'}`}>
                          {r.text}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-xs text-ink-2 tabular-nums">
                            {isWake ? 'когда проснёшься' : formatTime(r.remind_at!)}
                          </p>
                          {r.recurrence && (
                            <span className="flex items-center gap-0.5 text-[11px] text-accent">
                              <RefreshCw size={10} />
                              {RECURRENCE_LABEL[r.recurrence]}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Cancel */}
                      <button
                        onClick={() => handleCancel(r)}
                        className="btn-active shrink-0 text-ink-2 p-1 -mr-0.5 rounded hover:text-danger transition-colors"
                        aria-label="Отменить"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
